import React from "react";
import clsx from "clsx";

interface MyLabelProps {
  label: string | React.ReactNode;
  color?: "blue" | "green" | "red" | "yellow" | "purple" | "gray" | "indigo" | "pink" | "teal" | string;
  variant?: "solid" | "soft" | "outline";
  size?: "sm" | "md" | "lg";
  icon?: React.ReactNode;
  rounded?: "none" | "sm" | "md" | "lg" | "xl" | "full";
  uppercase?: boolean;
  bold?: boolean;
  shadow?: boolean;
  className?: string;
}

/**
 * A flexible, reusable label component with many customization options.
 *
 * Examples:
 * <MyLabel label="Active" color="green" variant="solid" />
 * <MyLabel label="Pending" color="yellow" variant="soft" icon={<ClockIcon />} />
 * <MyLabel label="Error" color="red" variant="outline" rounded="full" uppercase />
 */
export default function MyLabel({
  label,
  color = "gray",
  variant = "soft",
  size = "md",
  icon,
  rounded = "md",
  uppercase = false,
  bold = false,
  shadow = false,
  className,
}: MyLabelProps) {
  // Color maps for Tailwind classes
  const colorMap: Record<string, string> = {
    blue: "blue",
    green: "green",
    red: "red",
    yellow: "yellow",
    purple: "purple",
    gray: "gray",
    indigo: "indigo",
    pink: "pink",
    teal: "teal",
  };

  const chosenColor = colorMap[color] || color;

  const variantClasses = {
    solid: `bg-${chosenColor}-600 text-white`,
    soft: `bg-${chosenColor}-100 text-${chosenColor}-800 dark:bg-${chosenColor}-900/30 dark:text-${chosenColor}-300`,
    outline: `border border-${chosenColor}-400 text-${chosenColor}-700 dark:text-${chosenColor}-300`,
  }[variant];

  const sizeClasses = {
    sm: "text-xs px-2 py-0.5",
    md: "text-sm px-3 py-1",
    lg: "text-base px-4 py-1.5",
  }[size];

  return (
    <div
      className={clsx(
        "inline-flex items-center gap-1 select-none transition-all",
        variantClasses,
        sizeClasses,
        shadow && "shadow-sm",
        bold && "font-semibold",
        uppercase && "uppercase tracking-wide",
        `rounded-${rounded}`,
        className
      )}
    >
      {icon && <span className="text-sm">{icon}</span>}
      <span>{label}</span>
    </div>
  );
}